import { getStaticEventName, STATIC_EVENT_MAPPINGS } from './prop-flags';
export function applyEvents(element, nodeId, props, eventHandlers, inputStates) {
    const existing = eventHandlers.get(nodeId) ?? new Map();
    for (const [propName, eventName] of STATIC_EVENT_MAPPINGS) {
        const handler = props[propName];
        const prev = existing.get(eventName);
        if (prev) {
            element.removeEventListener(eventName, prev);
            existing.delete(eventName);
        }
        if (handler) {
            element.addEventListener(eventName, handler);
            existing.set(eventName, handler);
        }
    }
    syncCompositionEvents(element, nodeId, props, existing, inputStates);
    updateInputEvent(element, nodeId, props, existing, inputStates);
    updateChangeEvent(element, nodeId, props, existing, inputStates);
    commitEventMap(nodeId, existing, eventHandlers);
}
export function applyEventProp(element, nodeId, props, propName, eventHandlers, inputStates) {
    const existing = eventHandlers.get(nodeId) ?? new Map();
    if (propName === 'onInput') {
        syncCompositionEvents(element, nodeId, props, existing, inputStates);
        updateInputEvent(element, nodeId, props, existing, inputStates);
        commitEventMap(nodeId, existing, eventHandlers);
        return;
    }
    if (propName === 'onChange') {
        updateChangeEvent(element, nodeId, props, existing, inputStates);
        commitEventMap(nodeId, existing, eventHandlers);
        return;
    }
    const eventName = getStaticEventName(propName);
    if (!eventName) {
        applyEvents(element, nodeId, props, eventHandlers, inputStates);
        return;
    }
    const prev = existing.get(eventName);
    if (prev) {
        element.removeEventListener(eventName, prev);
        existing.delete(eventName);
    }
    const handler = props[propName];
    if (handler) {
        element.addEventListener(eventName, handler);
        existing.set(eventName, handler);
    }
    commitEventMap(nodeId, existing, eventHandlers);
}
function updateInputEvent(element, nodeId, props, existing, inputStates) {
    const prevInput = existing.get('input');
    if (prevInput) {
        element.removeEventListener('input', prevInput);
        existing.delete('input');
    }
    const onInput = props.onInput;
    if (typeof onInput !== 'function') {
        return;
    }
    const inputState = getInputState(nodeId, inputStates);
    const handler = onInput;
    const wrappedHandler = (event) => {
        const target = event.target;
        const targetValue = readValueTarget(target);
        if (targetValue !== null
            && inputState.pendingCompositionCommitValue !== null
            && targetValue === inputState.pendingCompositionCommitValue) {
            inputState.pendingCompositionCommitValue = null;
            return;
        }
        inputState.pendingCompositionCommitValue = null;
        if ((inputState.isComposing || isComposingInputEvent(event)) && isCompositionTarget(target)) {
            return;
        }
        if (targetValue !== null) {
            handler(targetValue);
        }
        else {
            onInput(event);
        }
    };
    element.addEventListener('input', wrappedHandler);
    existing.set('input', wrappedHandler);
}
function syncCompositionEvents(element, nodeId, props, existing, inputStates) {
    const prevCompositionStart = existing.get('compositionstart');
    if (prevCompositionStart) {
        element.removeEventListener('compositionstart', prevCompositionStart);
        existing.delete('compositionstart');
    }
    const prevCompositionEnd = existing.get('compositionend');
    if (prevCompositionEnd) {
        element.removeEventListener('compositionend', prevCompositionEnd);
        existing.delete('compositionend');
    }
    if (!isCompositionTarget(element)) {
        return;
    }
    const inputState = getInputState(nodeId, inputStates);
    const onInput = props.onInput;
    const handleCompositionStart = () => {
        inputState.isComposing = true;
        inputState.pendingCompositionCommitValue = null;
    };
    const handleCompositionEnd = (event) => {
        inputState.isComposing = false;
        const target = event.target;
        const targetValue = readValueTarget(target);
        if (targetValue !== null) {
            inputState.pendingCompositionCommitValue = targetValue;
            if (typeof onInput === 'function') {
                onInput(targetValue);
            }
        }
    };
    element.addEventListener('compositionstart', handleCompositionStart);
    existing.set('compositionstart', handleCompositionStart);
    element.addEventListener('compositionend', handleCompositionEnd);
    existing.set('compositionend', handleCompositionEnd);
}
function updateChangeEvent(element, nodeId, props, existing, inputStates) {
    const prevChange = existing.get('change');
    if (prevChange) {
        element.removeEventListener('change', prevChange);
        existing.delete('change');
    }
    const onChange = props.onChange;
    if (typeof onChange !== 'function') {
        return;
    }
    const inputState = getInputState(nodeId, inputStates);
    if (isCheckableInput(element)) {
        inputState.checkedSnapshot = element.checked;
    }
    const handler = onChange;
    const wrappedHandler = (event) => {
        const target = event.target;
        if (target?.tagName === 'INPUT' && target.type === 'checkbox') {
            const checkbox = target;
            if (isTruthy(props.readOnly)) {
                event.preventDefault();
                restoreReadOnlyCheckedState(checkbox, props, inputState);
                if (props.indeterminate !== undefined) {
                    checkbox.indeterminate = readBooleanProp(props.indeterminate);
                }
                return;
            }
            if (checkbox.indeterminate) {
                checkbox.indeterminate = false;
            }
            inputState.checkedSnapshot = checkbox.checked;
            handler(Boolean(checkbox.checked), event);
        }
        else if (target?.tagName === 'INPUT' && target.type === 'radio') {
            const radio = target;
            if (isTruthy(props.readOnly)) {
                event.preventDefault();
                restoreReadOnlyCheckedState(radio, props, inputState);
                return;
            }
            inputState.checkedSnapshot = radio.checked;
            handler(String(radio.value), event);
        }
        else {
            const targetValue = readValueTarget(target);
            if (targetValue !== null) {
                handler(targetValue, event);
                return;
            }
            onChange(event);
        }
    };
    element.addEventListener('change', wrappedHandler);
    existing.set('change', wrappedHandler);
}
function isCheckableInput(element) {
    return element.tagName === 'INPUT'
        && (element.type === 'checkbox' || element.type === 'radio');
}
function restoreReadOnlyCheckedState(target, props, inputState) {
    if (hasOwn(props, 'checked')) {
        target.checked = readBooleanProp(props.checked);
        inputState.checkedSnapshot = target.checked;
        return;
    }
    if (inputState.checkedSnapshot !== null) {
        target.checked = inputState.checkedSnapshot;
    }
}
function hasOwn(object, key) {
    return Object.prototype.hasOwnProperty.call(object, key);
}
function isTruthy(value) {
    return value === true || value === 'true';
}
function readBooleanProp(value) {
    return Boolean(typeof value === 'function' ? value() : value);
}
function readValueTarget(target) {
    if (target && 'value' in target) {
        return String(target.value);
    }
    return null;
}
function isComposingInputEvent(event) {
    return Boolean(event.isComposing);
}
function commitEventMap(nodeId, existing, eventHandlers) {
    if (existing.size > 0) {
        eventHandlers.set(nodeId, existing);
    }
    else {
        eventHandlers.delete(nodeId);
    }
}
function getInputState(nodeId, inputStates) {
    const existing = inputStates.get(nodeId);
    if (existing) {
        return existing;
    }
    const created = {
        isComposing: false,
        pendingCompositionCommitValue: null,
        checkedSnapshot: null,
    };
    inputStates.set(nodeId, created);
    return created;
}
function isCompositionTarget(element) {
    return element?.tagName === 'INPUT' || element?.tagName === 'TEXTAREA';
}
//# sourceMappingURL=events.js.map